import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import {
  Monitor, Code, Wrench, Buildings, Package, Desk, GraduationCap,
  Briefcase, PlusCircle, UsersFour, Gear, Certificate, BookOpen,
  ShieldCheck, Handshake, Trash, CurrencyEur, CurrencyDollar,
  Plus, Calculator, TrendUp, ChartBar, FileDoc, DownloadSimple,
  CaretDown, Info, Lightning, Leaf, Clock, Users, ArrowRight, Gauge,
} from '@phosphor-icons/react';
import economicsAPI, {
  CapexItem, OpexItem, InvestmentData, CBAResult, CashFlowYear, ScenarioComparisonResult,
} from '../../services/economicsService';

// ═══════════════════════════════════════════════════════════════
//  Constants & Category Maps
// ═══════════════════════════════════════════════════════════════

const CAPEX_CATS: Record<string, { label_es: string; label_en: string; icon: React.ElementType; color: string }> = {
  hardware:        { label_es: 'Hardware',           label_en: 'Hardware',           icon: Monitor,       color: '#38bdf8' },
  software:        { label_es: 'Software',           label_en: 'Software',           icon: Code,          color: '#a78bfa' },
  technical_works: { label_es: 'Trabajos técnicos',  label_en: 'Technical Works',    icon: Wrench,        color: '#fbbf24' },
  civil_works:     { label_es: 'Obra civil',         label_en: 'Civil Works',        icon: Buildings,     color: '#a8a29e' },
  supplies:        { label_es: 'Suministros',        label_en: 'Supplies',           icon: Package,       color: '#2dd4bf' },
  furniture:       { label_es: 'Mobiliario',         label_en: 'Furniture',          icon: Desk,          color: '#818cf8' },
  training:        { label_es: 'Formación',          label_en: 'Training',           icon: GraduationCap, color: '#34d399' },
  consulting:      { label_es: 'Consultoría',        label_en: 'Consulting',         icon: Briefcase,     color: '#F26430' },
  other:           { label_es: 'Otros',              label_en: 'Other',              icon: PlusCircle,    color: '#9ca3af' },
};

const OPEX_CATS: Record<string, { label_es: string; label_en: string; icon: React.ElementType; color: string }> = {
  personnel:          { label_es: 'Personal',              label_en: 'Personnel',           icon: UsersFour,  color: '#60a5fa' },
  maintenance:        { label_es: 'Mantenimiento',         label_en: 'Maintenance',         icon: Gear,       color: '#fbbf24' },
  licenses:           { label_es: 'Licencias',             label_en: 'Licenses',            icon: Certificate,color: '#a78bfa' },
  training_recurring: { label_es: 'Formación recurrente',  label_en: 'Recurring Training',  icon: BookOpen,   color: '#34d399' },
  insurance:          { label_es: 'Seguros',               label_en: 'Insurance',           icon: ShieldCheck,color: '#fb7185' },
  supplies:           { label_es: 'Suministros',           label_en: 'Supplies',            icon: Package,    color: '#2dd4bf' },
  external_services:  { label_es: 'Servicios externos',    label_en: 'External Services',   icon: Handshake,  color: '#F26430' },
  other:              { label_es: 'Otros',                 label_en: 'Other',               icon: PlusCircle, color: '#9ca3af' },
};

const TABS = [
  { id: 'investment', icon: Calculator, label_es: 'Inversión', label_en: 'Investment' },
  { id: 'benefits',   icon: TrendUp,    label_es: 'Beneficios', label_en: 'Benefits' },
  { id: 'roi',        icon: ChartBar,   label_es: 'ROI & CBA', label_en: 'ROI & CBA' },
  { id: 'sensitivity',icon: Lightning,  label_es: 'Sensibilidad', label_en: 'Sensitivity' },
  { id: 'scenarios',  icon: ArrowRight, label_es: 'Escenarios', label_en: 'Scenarios' },
  { id: 'capacity',   icon: Gauge, label_es: 'Capacidad liberada', label_en: 'Liberated Capacity' },
  { id: 'summary',    icon: FileDoc,    label_es: 'Resumen Ejecutivo', label_en: 'Exec Summary' },
];

const LABOR_REFS: Record<string, { label: string; pct: string }> = {
  ES: { label: 'España', pct: '30-33%' },
  DE: { label: 'Alemania', pct: '19-21%' },
  FR: { label: 'Francia', pct: '40-45%' },
  GB: { label: 'UK', pct: '13-15%' },
  IT: { label: 'Italia', pct: '30-32%' },
  PT: { label: 'Portugal', pct: '23-24%' },
  US: { label: 'USA', pct: '7-10%' },
  AE: { label: 'Emiratos', pct: '12-15%' },
};

const fmt = (n: number, currency = 'EUR') => {
  const sym = currency === 'EUR' ? '€' : '$';
  return `${sym}${n.toLocaleString('es-ES', { maximumFractionDigits: 0 })}`;
};

interface EconomicsPanelProps {
  projectId: string;
  projectName?: string;
}

// ═══════════════════════════════════════════════════════════════
//  Main Component
// ═══════════════════════════════════════════════════════════════

const EconomicsPanel: React.FC<EconomicsPanelProps> = ({ projectId, projectName }) => {
  const { language } = useLanguage();
  const t = (es: string, en: string) => language === 'es' ? es : en;

  const [activeTab, setActiveTab] = useState('investment');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);

  // Investment state
  const [currency, setCurrency] = useState<'EUR' | 'USD'>('EUR');
  const [capexItems, setCapexItems] = useState<CapexItem[]>([]);
  const [opexItems, setOpexItems] = useState<OpexItem[]>([]);
  const [totalCapex, setTotalCapex] = useState(0);
  const [totalOpex, setTotalOpex] = useState(0);

  // CBA state
  const [cbaResults, setCbaResults] = useState<Record<string, CBAResult>>({});
  const [benefits, setBenefits] = useState<Record<string, any>>({});
  const [selectedHorizon, setSelectedHorizon] = useState('10');
  const [sensitivity, setSensitivity] = useState<any>(null);
  const [maturityFactors, setMaturityFactors] = useState<any[]>([]);

  // Project config
  const [projectType, setProjectType] = useState<'new_sdp' | 'sdp_improvement'>('new_sdp');
  const [rampUpMonths, setRampUpMonths] = useState(18);

  // Scenario comparison
  const [scenarioData, setScenarioData] = useState<any>(null);
  const [scenarioLoading, setScenarioLoading] = useState(false);

  // Liberated-capacity revenue channel (measured taxi ceiling → freed movements → revenue)
  const [capVal, setCapVal] = useState<any>(null);
  const [capValLoading, setCapValLoading] = useState(false);
  const [capValInputs, setCapValInputs] = useState({
    rate_per_movement: '', commercial_revenue_per_pax: '',
    delay89_reduced_min: '', saturated_share: '',
  });

  const runCapacityValue = async () => {
    setCapValLoading(true);
    try {
      const body: Record<string, any> = {};
      const rpm = parseFloat(capValInputs.rate_per_movement);
      const cpp = parseFloat(capValInputs.commercial_revenue_per_pax);
      const d89 = parseFloat(capValInputs.delay89_reduced_min);
      const sat = parseFloat(capValInputs.saturated_share);
      if (!isNaN(rpm)) body.rate_per_movement = rpm;
      if (!isNaN(cpp)) body.commercial_revenue_per_pax = cpp;
      if (!isNaN(d89)) body.delay89_reduced_min = d89;
      if (!isNaN(sat)) body.saturated_share = sat;
      body.currency = currency;
      const data = await economicsAPI.getCapacityValue(projectId, body);
      setCapVal(data);
    } catch (e) {
      setCapVal({ available: false, reason: 'request failed' });
    } finally {
      setCapValLoading(false);
    }
  };

  // ── Load data ──
  useEffect(() => {
    loadEconomics();
  }, [projectId]);

  const loadEconomics = async () => {
    setLoading(true);
    try {
      const res = await economicsAPI.getEconomics(projectId);
      const data = res.data || {};
      if (data.investment) {
        setCapexItems(data.investment.capex_items || []);
        setOpexItems(data.investment.opex_items || []);
        setTotalCapex(data.investment.total_capex || 0);
        setTotalOpex(data.investment.total_opex_annual || 0);
        setCurrency(data.investment.project_currency || 'EUR');
      }
      if (data.cba_results) setCbaResults(data.cba_results);
      if (data.benefits) setBenefits(data.benefits);
      if (data.parameters) {
        setProjectType(data.parameters.project_type || 'new_sdp');
        setRampUpMonths(data.parameters.ramp_up_months || 18);
      }
      if (data.maturity_factors) setMaturityFactors(data.maturity_factors);
    } catch (e) { console.error('Failed to load economics:', e); }
    setLoading(false);
  };

  // ── CAPEX helpers ──
  const addCapexItem = () => {
    setCapexItems(prev => [...prev, {
      id: crypto.randomUUID().slice(0, 8),
      category: 'hardware', description: '', unit_cost: 0, currency, quantity: 1, total: 0,
    }]);
  };

  const updateCapexItem = (idx: number, field: string, value: any) => {
    setCapexItems(prev => {
      const items = [...prev];
      (items[idx] as any)[field] = value;
      items[idx].total = items[idx].unit_cost * items[idx].quantity;
      return items;
    });
  };

  const removeCapexItem = (idx: number) => {
    setCapexItems(prev => prev.filter((_, i) => i !== idx));
  };

  // ── OPEX helpers ──
  const addOpexItem = () => {
    setOpexItems(prev => [...prev, {
      id: crypto.randomUUID().slice(0, 8),
      category: 'maintenance', description: '', unit_cost: 0, currency, quantity: 1,
      labor_cost_pct: 0, total: 0,
    }]);
  };

  const updateOpexItem = (idx: number, field: string, value: any) => {
    setOpexItems(prev => {
      const items = [...prev];
      (items[idx] as any)[field] = value;
      if (items[idx].category === 'personnel' && items[idx].labor_cost_pct > 0) {
        items[idx].total = items[idx].unit_cost * items[idx].quantity * (1 + items[idx].labor_cost_pct / 100);
      } else {
        items[idx].total = items[idx].unit_cost * items[idx].quantity;
      }
      return items;
    });
  };

  const removeOpexItem = (idx: number) => {
    setOpexItems(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Calculate totals ──
  const calculateCapex = () => {
    const total = capexItems.reduce((sum, i) => sum + (i.unit_cost * i.quantity), 0);
    setTotalCapex(total);
    // Recalc all items
    setCapexItems(prev => prev.map(i => ({ ...i, total: i.unit_cost * i.quantity })));
  };

  const calculateOpex = () => {
    const total = opexItems.reduce((sum, i) => {
      if (i.category === 'personnel' && i.labor_cost_pct > 0) {
        return sum + i.unit_cost * i.quantity * (1 + i.labor_cost_pct / 100);
      }
      return sum + i.unit_cost * i.quantity;
    }, 0);
    setTotalOpex(total);
    setOpexItems(prev => prev.map(i => ({
      ...i,
      total: i.category === 'personnel' && i.labor_cost_pct > 0
        ? i.unit_cost * i.quantity * (1 + i.labor_cost_pct / 100)
        : i.unit_cost * i.quantity,
    })));
  };

  // ── Save ──
  const saveInvestment = async () => {
    setSaving(true);
    try {
      calculateCapex();
      calculateOpex();
      await economicsAPI.saveInvestment(projectId, {
        capex_items: capexItems,
        opex_items: opexItems,
        project_currency: currency,
        exchange_rate: 1.08,
      });
    } catch (e) { console.error('Save failed:', e); }
    setSaving(false);
  };

  // ── Run CBA ──
  const runCBA = async () => {
    setCalculating(true);
    try {
      await saveInvestment();
      const res = await economicsAPI.calculateCBA(projectId, {
        project_type: projectType,
        ramp_up_months: rampUpMonths,
        curve_type: 'sigmoid',
      });
      const data = res.data;
      setCbaResults(data.cba_results || {});
      setBenefits(data.benefits || {});
      setMaturityFactors(data.maturity_factors || []);
    } catch (e) { console.error('CBA failed:', e); }
    setCalculating(false);
  };

  // ── Run scenario comparison ──
  const runScenarios = async () => {
    setScenarioLoading(true);
    try {
      const res = await economicsAPI.getScenarioComparison(projectId);
      setScenarioData(res);
    } catch (e) { console.error('Scenario comparison failed:', e); }
    setScenarioLoading(false);
  };

  // ── Render helpers ──
  const sym = currency === 'EUR' ? '€' : '$';
  const personnelSubtotal = opexItems.filter(i => i.category === 'personnel').reduce((s, i) => s + i.total, 0);
  const otherSubtotal = opexItems.filter(i => i.category !== 'personnel').reduce((s, i) => s + i.total, 0);

  const currentCBA = cbaResults[selectedHorizon];

  // ═══════════════════════════════════════════════════════════
  //  Render
  // ═══════════════════════════════════════════════════════════

  return (
    <div style={{ background: 'var(--surface)', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>

      {/* ── Tab Bar ── */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', background: 'var(--bg)', overflowX: 'auto' }}>
        {TABS.map(tab => {
          const active = activeTab === tab.id;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: '1 1 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                padding: '14px 16px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 500,
                color: active ? 'var(--aipron-accent)' : 'var(--text-secondary)',
                background: active ? 'var(--surface)' : 'transparent',
                borderBottom: active ? '2px solid var(--aipron-accent)' : '2px solid transparent',
                transition: 'all 150ms',
              }}
            >
              <Icon size={18} weight={active ? 'fill' : 'regular'} />
              {language === 'es' ? tab.label_es : tab.label_en}
            </button>
          );
        })}
      </div>

      {/* ── Tab Content ── */}
      <div style={{ padding: 24 }}>

        {/* ═══════ TAB 1: Investment ═══════ */}
        {activeTab === 'investment' && (
          <div>
            {/* Currency Toggle + Project Type */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {t('Moneda', 'Currency')}
                </span>
                <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
                  {(['EUR', 'USD'] as const).map(c => (
                    <button key={c} onClick={() => setCurrency(c)} style={{
                      padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                      background: currency === c ? 'var(--aipron-accent)' : 'var(--surface)',
                      color: currency === c ? '#fff' : 'var(--text-secondary)',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      {c === 'EUR' ? <CurrencyEur size={16} /> : <CurrencyDollar size={16} />}
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--text-tertiary)', fontWeight: 600 }}>{t('Tipo proyecto', 'Project type')}</span>
                <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
                  {([
                    { val: 'new_sdp' as const, label_es: 'SDP Nuevo', label_en: 'New SDP' },
                    { val: 'sdp_improvement' as const, label_es: 'Mejora SDP', label_en: 'SDP Improvement' },
                  ]).map(opt => (
                    <button key={opt.val} onClick={() => setProjectType(opt.val)} style={{
                      padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      background: projectType === opt.val ? 'var(--aipron-accent)' : 'var(--surface)',
                      color: projectType === opt.val ? '#fff' : 'var(--text-secondary)',
                    }}>
                      {language === 'es' ? opt.label_es : opt.label_en}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Ramp-up slider */}
            <div style={{ marginBottom: 24, padding: '12px 16px', borderRadius: 10, background: 'var(--bg)', border: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                  {t('Periodo Ramp-up', 'Ramp-up Period')}: <strong>{rampUpMonths} {t('meses', 'months')}</strong>
                </span>
              </div>
              <input
                type="range" min={6} max={24} value={rampUpMonths}
                onChange={e => setRampUpMonths(Number(e.target.value))}
                style={{ width: '100%', accentColor: 'var(--aipron-accent)' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-tertiary)' }}>
                <span>6m</span><span>12m</span><span>18m</span><span>24m</span>
              </div>
            </div>

            {/* ── CAPEX Section ── */}
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <TrendUp size={20} weight="bold" style={{ color: 'var(--aipron-accent)' }} />
                CAPEX — {t('Inversión Inicial', 'Initial Investment')}
              </h3>

              {/* Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)' }}>
                      {[t('Tipo', 'Type'), t('Descripción', 'Description'), t('Coste ud.', 'Unit Cost'), t('Uds', 'Qty'), 'Total', ''].map((h, i) => (
                        <th key={i} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {capexItems.map((item, idx) => {
                      const cat = CAPEX_CATS[item.category] || CAPEX_CATS.other;
                      const CatIcon = cat.icon;
                      return (
                        <tr key={item.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          <td style={{ padding: '8px 12px', minWidth: 160 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <CatIcon size={16} style={{ color: cat.color, flexShrink: 0 }} />
                              <select value={item.category} onChange={e => updateCapexItem(idx, 'category', e.target.value)}
                                style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px', color: 'var(--text-primary)', fontSize: 12, width: '100%' }}>
                                {Object.entries(CAPEX_CATS).map(([k, v]) => (
                                  <option key={k} value={k}>{language === 'es' ? v.label_es : v.label_en}</option>
                                ))}
                              </select>
                            </div>
                          </td>
                          <td style={{ padding: '8px 12px', minWidth: 200 }}>
                            <input value={item.description} onChange={e => updateCapexItem(idx, 'description', e.target.value)}
                              placeholder={t('Descripción...', 'Description...')}
                              style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px', color: 'var(--text-primary)', fontSize: 13 }} />
                          </td>
                          <td style={{ padding: '8px 12px', minWidth: 120 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{sym}</span>
                              <input type="number" value={item.unit_cost || ''} onChange={e => updateCapexItem(idx, 'unit_cost', Number(e.target.value))}
                                style={{ width: 100, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 8px', color: 'var(--text-primary)', fontSize: 13, textAlign: 'right' }} />
                            </div>
                          </td>
                          <td style={{ padding: '8px 12px', width: 70 }}>
                            <input type="number" min={1} value={item.quantity || ''} onChange={e => updateCapexItem(idx, 'quantity', Number(e.target.value))}
                              style={{ width: 60, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 8px', color: 'var(--text-primary)', fontSize: 13, textAlign: 'center' }} />
                          </td>
                          <td style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--text-primary)', textAlign: 'right', minWidth: 100 }}>
                            {fmt(item.unit_cost * item.quantity, currency)}
                          </td>
                          <td style={{ padding: '8px 4px', width: 36 }}>
                            <button onClick={() => removeCapexItem(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 4 }}
                              onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')} onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-tertiary)')}>
                              <Trash size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Add + Calculate buttons */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
                <button onClick={addCapexItem} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
                  background: 'var(--bg)', border: '1px dashed var(--border)', cursor: 'pointer',
                  color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600,
                }}>
                  <Plus size={16} /> {t('Agregar concepto', 'Add item')}
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                    TOTAL CAPEX: {fmt(capexItems.reduce((s, i) => s + i.unit_cost * i.quantity, 0), currency)}
                  </span>
                  <button onClick={calculateCapex} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', borderRadius: 8,
                    background: 'var(--aipron-accent)', border: 'none', cursor: 'pointer',
                    color: '#fff', fontSize: 13, fontWeight: 700,
                  }}>
                    <Calculator size={16} /> {t('Calcular CAPEX', 'Calculate CAPEX')}
                  </button>
                </div>
              </div>
            </div>

            {/* ── OPEX Section ── */}
            <div style={{ marginBottom: 24 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Clock size={20} weight="bold" style={{ color: '#60a5fa' }} />
                OPEX — {t('Costes Operativos Anuales', 'Annual Operating Costs')}
              </h3>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)' }}>
                      {[t('Tipo', 'Type'), t('Descripción', 'Description'), t('Coste ud.', 'Unit Cost'), t('Uds/FTEs', 'Qty/FTEs'), t('Cot.(%)', 'Lab.(%)'), 'Total', ''].map((h, i) => (
                        <th key={i} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {opexItems.map((item, idx) => {
                      const cat = OPEX_CATS[item.category] || OPEX_CATS.other;
                      const CatIcon = cat.icon;
                      const isPersonnel = item.category === 'personnel';
                      const itemTotal = isPersonnel && item.labor_cost_pct > 0
                        ? item.unit_cost * item.quantity * (1 + item.labor_cost_pct / 100)
                        : item.unit_cost * item.quantity;

                      return (
                        <tr key={item.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          <td style={{ padding: '8px 12px', minWidth: 160 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <CatIcon size={16} style={{ color: cat.color, flexShrink: 0 }} />
                              <select value={item.category} onChange={e => updateOpexItem(idx, 'category', e.target.value)}
                                style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px', color: 'var(--text-primary)', fontSize: 12, width: '100%' }}>
                                {Object.entries(OPEX_CATS).map(([k, v]) => (
                                  <option key={k} value={k}>{language === 'es' ? v.label_es : v.label_en}</option>
                                ))}
                              </select>
                            </div>
                          </td>
                          <td style={{ padding: '8px 12px', minWidth: 180 }}>
                            <input value={item.description} onChange={e => updateOpexItem(idx, 'description', e.target.value)}
                              placeholder={isPersonnel ? t('Cargo / Puesto', 'Position') : t('Descripción...', 'Description...')}
                              style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px', color: 'var(--text-primary)', fontSize: 13 }} />
                          </td>
                          <td style={{ padding: '8px 12px', minWidth: 120 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{sym}</span>
                              <input type="number" value={item.unit_cost || ''} onChange={e => updateOpexItem(idx, 'unit_cost', Number(e.target.value))}
                                style={{ width: 100, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 8px', color: 'var(--text-primary)', fontSize: 13, textAlign: 'right' }} />
                            </div>
                          </td>
                          <td style={{ padding: '8px 12px', width: 80 }}>
                            <input type="number" min={1} value={item.quantity || ''} onChange={e => updateOpexItem(idx, 'quantity', Number(e.target.value))}
                              style={{ width: 64, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 8px', color: 'var(--text-primary)', fontSize: 13, textAlign: 'center' }} />
                          </td>
                          <td style={{ padding: '8px 12px', width: 90 }}>
                            {isPersonnel ? (
                              <div style={{ position: 'relative' }}>
                                <input type="number" min={0} max={100} value={item.labor_cost_pct || ''} onChange={e => updateOpexItem(idx, 'labor_cost_pct', Number(e.target.value))}
                                  placeholder="33"
                                  style={{ width: 64, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 8px', color: 'var(--text-primary)', fontSize: 13, textAlign: 'center' }} />
                                <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--text-tertiary)' }}>%</span>
                              </div>
                            ) : (
                              <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '8px 12px', fontWeight: 600, color: 'var(--text-primary)', textAlign: 'right', minWidth: 100 }}>
                            {fmt(itemTotal, currency)}
                          </td>
                          <td style={{ padding: '8px 4px', width: 36 }}>
                            <button onClick={() => removeOpexItem(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 4 }}
                              onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')} onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-tertiary)')}>
                              <Trash size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Labor cost tooltip */}
              <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, background: 'var(--bg)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <Info size={16} style={{ color: 'var(--aipron-accent)', flexShrink: 0, marginTop: 2 }} />
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
                  <strong>{t('Referencia cotizaciones patronales:', 'Employer contribution reference:')}</strong>{' '}
                  {Object.entries(LABOR_REFS).map(([k, v]) => `${v.label}: ${v.pct}`).join(' · ')}
                </div>
              </div>

              {/* Add + Calculate OPEX */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, flexWrap: 'wrap', gap: 8 }}>
                <button onClick={addOpexItem} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8,
                  background: 'var(--bg)', border: '1px dashed var(--border)', cursor: 'pointer',
                  color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600,
                }}>
                  <Plus size={16} /> {t('Agregar concepto', 'Add item')}
                </button>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('Subtotal Personal', 'Personnel Subtotal')}: {fmt(personnelSubtotal, currency)}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('Subtotal Otros', 'Other Subtotal')}: {fmt(otherSubtotal, currency)}</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                    TOTAL OPEX {t('ANUAL', 'ANNUAL')}: {fmt(personnelSubtotal + otherSubtotal, currency)}
                  </span>
                  <button onClick={calculateOpex} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 20px', borderRadius: 8, marginTop: 4,
                    background: '#60a5fa', border: 'none', cursor: 'pointer', color: '#fff', fontSize: 13, fontWeight: 700,
                  }}>
                    <Calculator size={16} /> {t('Calcular OPEX', 'Calculate OPEX')}
                  </button>
                </div>
              </div>
            </div>

            {/* ── Run CBA Button ── */}
            <div style={{ textAlign: 'center', paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <button onClick={runCBA} disabled={calculating || capexItems.length === 0}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 40px', borderRadius: 12,
                  background: calculating ? 'var(--text-tertiary)' : 'linear-gradient(135deg, #F26430, #D85528)',
                  border: 'none', cursor: calculating ? 'not-allowed' : 'pointer',
                  color: '#fff', fontSize: 15, fontWeight: 700, boxShadow: '0 4px 16px rgba(242,100,48,0.3)',
                  transition: 'all 200ms',
                }}>
                <ChartBar size={20} weight="bold" />
                {calculating ? t('Calculando...', 'Calculating...') : t('Ejecutar Análisis CBA', 'Run CBA Analysis')}
              </button>
            </div>
          </div>
        )}

        {/* ═══════ TAB 2: Benefits ═══════ */}
        {activeTab === 'benefits' && (
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 20 }}>
              {t('Beneficios Estimados', 'Estimated Benefits')}
            </h3>
            {!benefits.total_annual_benefits ? (
              <p style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: 40 }}>
                {t('Ejecuta el análisis CBA en la pestaña Inversión para ver los beneficios estimados.', 'Run the CBA analysis in the Investment tab to see estimated benefits.')}
              </p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                {[
                  { label: t('Ahorro combustible', 'Fuel Savings'), value: benefits.fuel_savings, icon: Lightning, color: '#fbbf24', source: 'Eurocontrol Ch.6+24' },
                  { label: t('Reducción emisiones (ETS)', 'Emissions Reduction (ETS)'), value: benefits.emissions_savings, icon: Leaf, color: '#34d399', source: 'Eurocontrol Ch.7+8' },
                  { label: t('Ahorro por delay', 'Delay Savings'), value: benefits.delay_savings, icon: Clock, color: '#f87171', source: 'Eurocontrol Ch.16' },
                  { label: t('Valor tiempo pasajero', 'Passenger Time Value'), value: benefits.passenger_value, icon: Users, color: '#60a5fa', source: 'Eurocontrol Ch.42' },
                ].map((b, i) => (
                  <div key={i} style={{ padding: 20, borderRadius: 12, background: 'var(--bg)', border: '1px solid var(--border-subtle)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <b.icon size={20} style={{ color: b.color }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>{b.label}</span>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(b.value || 0, currency)}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('Fuente', 'Source')}: {b.source}</div>
                  </div>
                ))}
                <div style={{ padding: 20, borderRadius: 12, background: 'linear-gradient(135deg, rgba(242,100,48,0.1), rgba(242,100,48,0.05))', border: '1px solid var(--aipron-accent)', gridColumn: 'span 2' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>{t('TOTAL BENEFICIOS ANUALES', 'TOTAL ANNUAL BENEFITS')}</span>
                  <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--aipron-accent)', marginTop: 4 }}>
                    {fmt(benefits.total_annual_benefits || 0, currency)}<span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)' }}> /{t('año', 'year')}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════ TAB 3: ROI & CBA ═══════ */}
        {activeTab === 'roi' && (
          <div>
            {/* Horizon selector */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>ROI & CBA</h3>
              <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
                {['1', '2', '5', '10'].map(h => (
                  <button key={h} onClick={() => setSelectedHorizon(h)} style={{
                    padding: '6px 16px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    background: selectedHorizon === h ? 'var(--aipron-accent)' : 'var(--surface)',
                    color: selectedHorizon === h ? '#fff' : 'var(--text-secondary)',
                  }}>
                    {h} {t('año', 'yr')}{Number(h) > 1 ? 's' : ''}
                  </button>
                ))}
              </div>
            </div>

            {!currentCBA ? (
              <p style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: 40 }}>
                {t('Ejecuta el análisis CBA primero.', 'Run the CBA analysis first.')}
              </p>
            ) : (
              <>
                {/* KPI Cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
                  {[
                    { label: 'NPV', value: fmt(currentCBA.npv, currency), color: currentCBA.npv >= 0 ? '#34d399' : '#f87171' },
                    { label: 'IRR', value: currentCBA.irr !== null ? `${currentCBA.irr}%` : 'N/A', color: (currentCBA.irr || 0) > 4 ? '#34d399' : '#fbbf24' },
                    { label: t('Payback', 'Payback'), value: currentCBA.payback_simple !== null ? `${currentCBA.payback_simple} ${t('años', 'yrs')}` : 'N/A', color: '#60a5fa' },
                    { label: 'BCR', value: `${currentCBA.bcr}`, color: currentCBA.bcr >= 1 ? '#34d399' : '#f87171' },
                    { label: 'ROI', value: currentCBA.total_capex > 0 ? `${Math.round((currentCBA.total_benefits - currentCBA.total_capex - currentCBA.total_opex) / currentCBA.total_capex * 100)}%` : 'N/A', color: 'var(--aipron-accent)' },
                  ].map((kpi, i) => (
                    <div key={i} style={{ padding: '16px 20px', borderRadius: 12, background: 'var(--bg)', border: '1px solid var(--border-subtle)', textAlign: 'center' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{kpi.label}</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: kpi.color, marginTop: 4 }}>{kpi.value}</div>
                    </div>
                  ))}
                </div>

                {/* Cash Flow Table */}
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border)' }}>
                        {[t('Año', 'Year'), 'CAPEX', 'OPEX', t('Beneficios', 'Benefits'), t('Neto', 'Net'), t('Acumulado', 'Cumulative'), t('Madurez', 'Maturity')].map((h, i) => (
                          <th key={i} style={{ padding: '8px 10px', textAlign: i === 0 ? 'center' : 'right', fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(currentCBA.cash_flows || []).map((cf: CashFlowYear) => (
                        <tr key={cf.year} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          <td style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 600 }}>{cf.year}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', color: cf.capex > 0 ? '#f87171' : 'var(--text-tertiary)' }}>{cf.capex > 0 ? fmt(cf.capex, currency) : '—'}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', color: cf.opex > 0 ? '#fbbf24' : 'var(--text-tertiary)' }}>{cf.opex > 0 ? fmt(cf.opex, currency) : '—'}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', color: '#34d399' }}>{cf.benefits > 0 ? fmt(cf.benefits, currency) : '—'}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: cf.net >= 0 ? '#34d399' : '#f87171' }}>{fmt(cf.net, currency)}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700, color: cf.cumulative >= 0 ? '#34d399' : '#f87171' }}>{fmt(cf.cumulative, currency)}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: cf.maturity_factor >= 1 ? 'rgba(52,211,153,0.15)' : 'rgba(251,191,36,0.15)', color: cf.maturity_factor >= 1 ? '#34d399' : '#fbbf24' }}>
                              {Math.round(cf.maturity_factor * 100)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ═══════ TAB 4: Sensitivity ═══════ */}
        {activeTab === 'sensitivity' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>{t('Análisis de Sensibilidad', 'Sensitivity Analysis')}</h3>
              <button onClick={async () => {
                try { const res = await economicsAPI.getSensitivity(projectId); setSensitivity(res.data); } catch (e) { console.error(e); }
              }} style={{
                padding: '8px 20px', borderRadius: 8, background: 'var(--aipron-accent)', border: 'none',
                cursor: 'pointer', color: '#fff', fontSize: 13, fontWeight: 700,
              }}>
                {t('Ejecutar Análisis', 'Run Analysis')}
              </button>
            </div>
            {!sensitivity ? (
              <p style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: 40 }}>
                {t('Pulsa "Ejecutar Análisis" para generar el análisis de sensibilidad.', 'Click "Run Analysis" to generate the sensitivity analysis.')}
              </p>
            ) : (
              <>
                {/* Scenarios */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
                  {(sensitivity.scenarios || []).map((s: any) => (
                    <div key={s.name} style={{ padding: 16, borderRadius: 12, background: 'var(--bg)', border: '1px solid var(--border-subtle)', textAlign: 'center' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 8 }}>
                        {language === 'es' ? s.label_es : s.label_en}
                      </div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: s.npv >= 0 ? '#34d399' : '#f87171' }}>NPV: {fmt(s.npv, currency)}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
                        IRR: {s.irr ?? 'N/A'}% · BCR: {s.bcr}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tornado (text-based since we don't have chart.js yet) */}
                <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>{t('Factores por impacto (Tornado)', 'Factors by Impact (Tornado)')}</h4>
                {(sensitivity.factors || []).map((f: any, i: number) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, padding: '8px 12px', borderRadius: 8, background: 'var(--bg)' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', minWidth: 150 }}>{f.name}</span>
                    <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--border-subtle)', position: 'relative', overflow: 'hidden' }}>
                      <div style={{ position: 'absolute', left: '50%', top: 0, height: '100%', width: `${Math.min(50, (f.npv_delta / (sensitivity.base_npv || 1)) * 50)}%`, background: 'var(--aipron-accent)', borderRadius: 3 }} />
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)', minWidth: 100, textAlign: 'right' }}>Δ NPV: {fmt(f.npv_delta, currency)}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* ═══════ TAB 5: Scenario Comparison ═══════ */}
        {activeTab === 'scenarios' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                  {t('Comparativa de Escenarios', 'Scenario Comparison')}
                </h3>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                  {t('As-Is vs. To-Be AMS vs. FASTGate vs. Infraestructura · VAN a 10 años',
                     'As-Is vs. To-Be AMS vs. FASTGate vs. Infrastructure · NPV at 10 years')}
                </p>
              </div>
              <button onClick={runScenarios} disabled={scenarioLoading} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px',
                borderRadius: 10, background: 'linear-gradient(135deg, #F26430, #D85528)',
                border: 'none', cursor: scenarioLoading ? 'not-allowed' : 'pointer',
                color: '#fff', fontSize: 13, fontWeight: 700,
                opacity: scenarioLoading ? 0.6 : 1,
              }}>
                <Calculator size={16} weight="bold" />
                {scenarioLoading ? t('Calculando...', 'Calculating...') : t('Calcular', 'Calculate')}
              </button>
            </div>

            {!scenarioData ? (
              <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-tertiary)' }}>
                <ChartBar size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
                <p>{t('Pulsa "Calcular" para generar la comparativa de los 4 escenarios.', 'Press "Calculate" to generate the 4-scenario comparison.')}</p>
              </div>
            ) : (
              <div>
                {/* KPI summary cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 24 }}>
                  <div style={{ padding: 14, borderRadius: 12, background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {t('Vuelos/año', 'Flights/year')}
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', marginTop: 4 }}>
                      {(scenarioData.annual_flights || 0).toLocaleString('es-ES')}
                    </div>
                  </div>
                  <div style={{ padding: 14, borderRadius: 12, background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {t('Mejor VAN (10a)', 'Best NPV (10y)')}
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: '#34d399', marginTop: 4 }}>
                      {fmt(Math.max(...(scenarioData.scenarios || []).map((s: any) => s.npv_10y)), 'EUR')}
                    </div>
                  </div>
                  <div style={{ padding: 14, borderRadius: 12, background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {t('Menor Payback', 'Fastest Payback')}
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--aipron-accent)', marginTop: 4 }}>
                      {(() => {
                        const pb = (scenarioData.scenarios || []).filter((s: any) => s.payback_years != null).map((s: any) => s.payback_years);
                        return pb.length > 0 ? `${Math.min(...pb)}a` : '—';
                      })()}
                    </div>
                  </div>
                </div>

                {/* Main comparison table */}
                <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid var(--border)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: 'var(--surface)' }}>
                        {[
                          t('Escenario', 'Scenario'),
                          t('Taxi-Out', 'Taxi-Out'),
                          t('Taxi-In', 'Taxi-In'),
                          t('Cap.+', 'Cap.+'),
                          t('Ahorro Combustible/año', 'Fuel Savings/yr'),
                          t('Ahorro Demoras/año', 'Delay Savings/yr'),
                          t('Ahorro Total/año', 'Total Savings/yr'),
                          'CAPEX',
                          'NPV 10a',
                          t('Payback', 'Payback'),
                          'BCR',
                        ].map((h, i) => (
                          <th key={i} style={{
                            padding: '10px 14px', textAlign: i === 0 ? 'left' : 'right',
                            fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)',
                            textTransform: 'uppercase', letterSpacing: '0.06em',
                            borderBottom: '1px solid var(--border)',
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(scenarioData.scenarios || []).map((sc: any, idx: number) => {
                        const isBaseline = sc.scenario_id === 'as_is';
                        const npvColor = sc.npv_10y > 0 ? '#34d399' : sc.npv_10y < 0 ? '#fb7185' : 'var(--text-secondary)';
                        return (
                          <tr key={sc.scenario_id} style={{
                            background: idx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)',
                            borderTop: '1px solid var(--border)',
                          }}>
                            <td style={{ padding: '12px 14px', fontWeight: 600, color: 'var(--text-primary)' }}>
                              {language === 'es' ? sc.label_es : sc.label_en}
                            </td>
                            <td style={{ padding: '12px 14px', textAlign: 'right', color: isBaseline ? 'var(--text-tertiary)' : '#34d399' }}>
                              {isBaseline ? '—' : `-${sc.delta_taxi_out_min} min`}
                            </td>
                            <td style={{ padding: '12px 14px', textAlign: 'right', color: isBaseline ? 'var(--text-tertiary)' : '#34d399' }}>
                              {isBaseline ? '—' : `-${sc.delta_taxi_in_min} min`}
                            </td>
                            <td style={{ padding: '12px 14px', textAlign: 'right', color: isBaseline ? 'var(--text-tertiary)' : '#60a5fa' }}>
                              {isBaseline ? '—' : `+${sc.delta_capacity_pct}%`}
                            </td>
                            <td style={{ padding: '12px 14px', textAlign: 'right', color: 'var(--text-primary)' }}>
                              {isBaseline ? '—' : fmt(sc.annual_fuel_savings, sc.currency)}
                            </td>
                            <td style={{ padding: '12px 14px', textAlign: 'right', color: 'var(--text-primary)' }}>
                              {isBaseline ? '—' : fmt(sc.annual_delay_savings, sc.currency)}
                            </td>
                            <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 700, color: isBaseline ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>
                              {isBaseline ? '—' : fmt(sc.annual_total_savings, sc.currency)}
                            </td>
                            <td style={{ padding: '12px 14px', textAlign: 'right', color: 'var(--text-secondary)' }}>
                              {isBaseline ? '—' : fmt(sc.capex_assumption, sc.currency)}
                            </td>
                            <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 700, color: npvColor }}>
                              {isBaseline ? '—' : fmt(sc.npv_10y, sc.currency)}
                            </td>
                            <td style={{ padding: '12px 14px', textAlign: 'right', color: 'var(--text-primary)' }}>
                              {isBaseline || sc.payback_years == null ? '—' : `${sc.payback_years}a`}
                            </td>
                            <td style={{ padding: '12px 14px', textAlign: 'right', color: sc.bcr >= 1 ? '#34d399' : '#fb7185', fontWeight: 600 }}>
                              {isBaseline ? '—' : sc.bcr.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <p style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 12 }}>
                  {t(
                    'CAPEX y ahorros son estimaciones basadas en datos DACAMS y benchmarks Eurocontrol/ACI. Personaliza en la pestaña Inversión.',
                    'CAPEX and savings are estimates based on DACAMS data and Eurocontrol/ACI benchmarks. Customise in the Investment tab.'
                  )}
                </p>
              </div>
            )}
          </div>
        )}

        {/* ═══════ TAB: Liberated Capacity (revenue channel) ═══════ */}
        {activeTab === 'capacity' && (
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>
              {t('Capacidad liberada → ingresos', 'Liberated capacity → revenue')}
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.5 }}>
              {t(
                'Convierte el techo de taxi-out MEDIDO (+ delay-89 pendiente del D-FOL) en movimientos liberados e ingresos del aeropuerto, por escenario de cierre. min_op = techo·cierre + delay89; mov/día = (salidas_sat/día · min_op) / ACT_TURN.',
                'Converts the MEASURED taxi-out ceiling (+ delay-89, pending the D-FOL) into freed movements and airport revenue, per closure scenario. min_op = ceiling·closure + delay89; mov/day = (saturated_dep/day · min_op) / ACT_TURN.'
              )}
            </p>

            {/* Manual tariff inputs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
              {[
                { k: 'rate_per_movement', es: 'Tasa por movimiento', en: 'Charge per movement', suffix: currency },
                { k: 'commercial_revenue_per_pax', es: 'Ingreso comercial / pax', en: 'Commercial revenue / pax', suffix: currency },
                { k: 'delay89_reduced_min', es: 'Delay-89 reducido (min/op)', en: 'Delay-89 reduced (min/op)', suffix: 'min' },
                { k: 'saturated_share', es: 'Cuota bandas saturadas (0–1)', en: 'Saturated-band share (0–1)', suffix: '' },
              ].map(f => (
                <div key={f.k}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 4 }}>
                    {t(f.es, f.en)}
                  </label>
                  <input
                    type="number" step="any"
                    value={(capValInputs as any)[f.k]}
                    onChange={e => setCapValInputs(prev => ({ ...prev, [f.k]: e.target.value }))}
                    placeholder={f.suffix}
                    style={{ width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)' }}
                  />
                </div>
              ))}
            </div>
            <button
              onClick={runCapacityValue}
              disabled={capValLoading}
              style={{ padding: '10px 16px', borderRadius: 8, border: 'none', background: 'var(--aipron-accent)', color: '#fff', fontWeight: 600, cursor: capValLoading ? 'default' : 'pointer', opacity: capValLoading ? 0.6 : 1, marginBottom: 16 }}
            >
              {capValLoading ? t('Calculando…', 'Calculating…') : t('Calcular ingresos liberados', 'Compute liberated revenue')}
            </button>

            {capVal && capVal.available === false && (
              <div style={{ padding: 12, borderRadius: 8, background: 'color-mix(in srgb, var(--aipron-warning) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--aipron-warning) 30%, transparent)', color: 'var(--aipron-warning)', fontSize: 13 }}>
                {t('No disponible: ', 'Unavailable: ')}{capVal.reason || t('faltan inputs medidos (techo de taxi, ACT_TURN, salidas).', 'missing measured inputs (taxi ceiling, ACT_TURN, departures).')}
              </div>
            )}

            {capVal && capVal.available && (
              <>
                {/* Measured inputs */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16, fontSize: 12, color: 'var(--text-secondary)' }}>
                  <span>{t('Techo taxi', 'Taxi ceiling')}: <b className="font-mono">{(capVal.taxi_ceiling?.ceiling_min_per_departure ?? capVal.inputs?.taxi_ceiling_min ?? '—')}</b> min/dep{capVal.taxi_ceiling?.ceiling_pct != null ? ` (${(capVal.taxi_ceiling.ceiling_pct * 100).toFixed(1)}%)` : ''}</span>
                  <span>ACT_TURN: <b className="font-mono">{capVal.inputs?.act_turn_median_min ?? '—'}</b> min</span>
                  <span>{t('Salidas/año', 'Departures/yr')}: <b className="font-mono">{capVal.inputs?.departures_per_year ?? '—'}</b></span>
                  <span>{t('Cuota saturada', 'Saturated share')}: <b className="font-mono">{capVal.inputs?.saturated_share ?? '—'}</b></span>
                </div>

                {/* Scenarios table */}
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-2, var(--surface))', textAlign: 'right' }}>
                      <th style={{ textAlign: 'left', padding: 8, color: 'var(--text-tertiary)' }}>{t('Cierre', 'Closure')}</th>
                      <th style={{ padding: 8, color: 'var(--text-tertiary)' }}>min/op</th>
                      <th style={{ padding: 8, color: 'var(--text-tertiary)' }}>{t('mov/día', 'mov/day')}</th>
                      <th style={{ padding: 8, color: 'var(--text-tertiary)' }}>{t('mov/año', 'mov/yr')}</th>
                      <th style={{ padding: 8, color: 'var(--text-tertiary)' }}>{t('Ingreso/año', 'Revenue/yr')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(capVal.scenarios || []).map((s: any, i: number) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--border)', textAlign: 'right' }}>
                        <td style={{ textAlign: 'left', padding: 8, fontWeight: 600 }}>{(s.closure_factor * 100).toFixed(0)}%</td>
                        <td className="font-mono" style={{ padding: 8 }}>{s.min_saved_per_op?.toFixed(2)}</td>
                        <td className="font-mono" style={{ padding: 8 }}>{s.mov_extra_per_day?.toFixed(2)}</td>
                        <td className="font-mono" style={{ padding: 8 }}>{Math.round(s.mov_extra_per_year).toLocaleString()}</td>
                        <td className="font-mono" style={{ padding: 8, color: 'var(--aipron-accent)' }}>
                          {s.airport_revenue_per_year != null ? fmt(s.airport_revenue_per_year, capVal.currency) : t('(sin tarifa)', '(no tariff)')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {(capVal.flags || []).length > 0 && (
                  <p style={{ marginTop: 12, fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
                    {t('Notas', 'Notes')}: {capVal.flags.join(' · ')}
                  </p>
                )}
                <p style={{ marginTop: 8, fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5 }}>
                  {t(
                    'Canal de ingresos = solo bandas saturadas. El canal de ahorros de aerolíneas (combustible/demora/CO₂, todas las salidas) está en la pestaña Beneficios.',
                    'Revenue channel = saturated bands only. The airline-savings channel (fuel/delay/CO₂, all departures) is in the Benefits tab.'
                  )}
                </p>
              </>
            )}
          </div>
        )}

        {/* ═══════ TAB 6: Executive Summary ═══════ */}
        {activeTab === 'summary' && (
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 20 }}>{t('Resumen Ejecutivo', 'Executive Summary')}</h3>
            {!currentCBA ? (
              <p style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: 40 }}>
                {t('Ejecuta el análisis CBA primero.', 'Run the CBA analysis first.')}
              </p>
            ) : (
              <div>
                <div style={{ padding: 24, borderRadius: 16, background: 'linear-gradient(135deg, rgba(242,100,48,0.08), rgba(242,100,48,0.02))', border: '1px solid var(--border)', marginBottom: 20 }}>
                  <p style={{ fontSize: 15, lineHeight: 1.8, color: 'var(--text-primary)' }}>
                    {t(
                      `La implementación del ${projectType === 'new_sdp' ? 'Servicio de Dirección en Plataforma' : 'mejora del SDP'} en el aeropuerto ${projectName || ''} requiere una inversión de ${fmt(totalCapex, currency)} con costes operativos anuales de ${fmt(totalOpex, currency)}. A un horizonte de 10 años, el proyecto presenta un VAN de ${fmt(cbaResults['10']?.npv || 0, currency)}, una TIR del ${cbaResults['10']?.irr || 'N/A'}% y un periodo de retorno de ${cbaResults['10']?.payback_simple || 'N/A'} años, con un ratio beneficio/coste de ${cbaResults['10']?.bcr || 'N/A'}.`,
                      `The implementation of the ${projectType === 'new_sdp' ? 'Platform Direction Service' : 'SDP improvement'} at ${projectName || ''} airport requires an investment of ${fmt(totalCapex, currency)} with annual operating costs of ${fmt(totalOpex, currency)}. Over a 10-year horizon, the project shows an NPV of ${fmt(cbaResults['10']?.npv || 0, currency)}, an IRR of ${cbaResults['10']?.irr || 'N/A'}%, and a payback period of ${cbaResults['10']?.payback_simple || 'N/A'} years, with a BCR of ${cbaResults['10']?.bcr || 'N/A'}.`
                    )}
                  </p>
                </div>

                <button onClick={() => economicsAPI.exportBusinessCase(projectId, language)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 32px', borderRadius: 12,
                    background: 'linear-gradient(135deg, #F26430, #D85528)', border: 'none', cursor: 'pointer',
                    color: '#fff', fontSize: 14, fontWeight: 700, boxShadow: '0 4px 16px rgba(242,100,48,0.3)',
                  }}>
                  <DownloadSimple size={20} weight="bold" />
                  {t('Exportar Business Case (.docx)', 'Export Business Case (.docx)')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EconomicsPanel;
